import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '@/shared/api/client';
import type { Envelope } from '@/shared/types/api';

/**
 * SUP-02 — the supplier's own session.
 *
 * Deliberately separate from the client session. A supplier user is not a user
 * of the buying organisation: they authenticate against a different guard,
 * carry no tenant permissions, and can reach nothing outside their own orders.
 * Sharing a session hook between the two would be one refactor away from
 * sharing an identity.
 */
export interface VendorSessionUser {
  security?: { password_min_length?: number | null };
  user: { id: string; name: string; email: string; locale: string | null; status: string };
  supplier: { id: string | null; display_name: string | null; status: string | null };
  /** The client this supplier trades with — never a list of them. */
  buyer: { slug: string | null; display_name: string | null };
  /** Portal-only capabilities. Deliberately NOT client permissions. */
  capabilities: {
    acknowledge_orders: boolean;
    submit_quotes: boolean;
    upload_invoices: boolean;
  };
}

export const VENDOR_SESSION_KEY = ['vendor', 'session'] as const;

export function useVendorSession() {
  const query = useQuery({
    queryKey: VENDOR_SESSION_KEY,
    queryFn: () => apiRequest<Envelope<VendorSessionUser>>('/vendor/me'),
    retry: false,
    staleTime: 60_000,
  });

  return {
    user: query.data?.data ?? null,
    isAuthenticated: query.data?.data !== undefined,
    isLoading: query.isPending,
  };
}

export function useVendorLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: { tenant: string; email: string; password: string }) =>
      apiRequest<Envelope<VendorSessionUser>>('/vendor/auth/login', {
        method: 'POST',
        body: credentials,
      }),
    // The password is this mutation's variables: gone as soon as the screen is
    // (as the tenant's and the operator's sign-in already do).
    gcTime: 0,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vendor'] }),
  });
}

export function useVendorLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiRequest<void>('/vendor/auth/logout', { method: 'POST' }),
    // Whatever the network says: a sign-out that failed (offline, a 5xx, an
    // expired CSRF cookie) must not leave this supplier's orders, enquiries
    // and session in memory for whoever uses the browser next — as the
    // tenant's sign-out (onSettled) and the operator's already do.
    onSettled: () => queryClient.clear(),
  });
}
